/**
 * ProjectDocs - Trang tai lieu du an
 * Redesigned with:
 * - 2 column layout on desktop, expand card chiếm full width
 * - Grid/List view toggle
 * - File preview modal with iframe
 * - Click subfolder to view contents
 * - Smooth animations
 */
import { useState, useEffect, useCallback } from 'react'
import { HiOutlineFolder, HiOutlineRefresh, HiOutlineSearch, HiOutlineX } from 'react-icons/hi'
import toast from 'react-hot-toast'
import driveService from '../services/driveService'
import { ProjectCard, FilePreviewModal, FileCard } from '../components/drive'
import SEO from '../components/common/SEO'

function ProjectDocs() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncStatus, setSyncStatus] = useState(null)

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searching, setSearching] = useState(false)

  // Preview modal state
  const [previewFile, setPreviewFile] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previewFileList, setPreviewFileList] = useState([]) // List of files for navigation
  const [previewIndex, setPreviewIndex] = useState(0)

  // Expanded project ID (chỉ 1 project được expand tại 1 thời điểm)
  const [expandedProjectId, setExpandedProjectId] = useState(null)

  // Load projects
  const loadProjects = useCallback(async () => {
    setLoading(true)
    try {
      const [projectsRes, statusRes] = await Promise.all([
        driveService.getProjects(),
        driveService.getSyncStatus()
      ])

      if (projectsRes.status === 'success') {
        setProjects(projectsRes.data || [])
      }
      if (statusRes.status === 'success') {
        setSyncStatus(statusRes.data)
      }
    } catch (error) {
      console.error('Failed to load projects:', error)
      toast.error('Khong the tai danh sach du an')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  // Handle search
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null)
      return
    }

    setSearching(true)
    try {
      const response = await driveService.search(query.trim())
      if (response.status === 'success') {
        setSearchResults(response.data || [])
      }
    } catch (error) {
      console.error('Search failed:', error)
    } finally {
      setSearching(false)
    }
  }, [])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, handleSearch])

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
  }

  // Handle copy link
  const handleCopyLink = async (file) => {
    const success = await driveService.copyLink(file)
    if (success) {
      toast.success('Da copy link!')
    } else {
      toast.error('Khong the copy link')
    }
  }

  // Handle preview with file list for navigation
  const handlePreviewFile = useCallback((file, fileList = []) => {
    setPreviewFile(file)
    setShowPreview(true)

    // If file list provided, enable navigation
    if (fileList.length > 0) {
      setPreviewFileList(fileList)
      const index = fileList.findIndex(f => f.id === file.id)
      setPreviewIndex(index >= 0 ? index : 0)
    } else {
      setPreviewFileList([])
      setPreviewIndex(0)
    }
  }, [])

  // Handle preview navigation
  const handlePreviewPrevious = useCallback(() => {
    if (previewIndex > 0) {
      const newIndex = previewIndex - 1
      setPreviewIndex(newIndex)
      setPreviewFile(previewFileList[newIndex])
    }
  }, [previewIndex, previewFileList])

  const handlePreviewNext = useCallback(() => {
    if (previewIndex < previewFileList.length - 1) {
      const newIndex = previewIndex + 1
      setPreviewIndex(newIndex)
      setPreviewFile(previewFileList[newIndex])
    }
  }, [previewIndex, previewFileList])

  // Handle preview from search results (with navigation)
  const handlePreviewSearchResult = useCallback((file) => {
    handlePreviewFile(file, searchResults || [])
  }, [handlePreviewFile, searchResults])

  // Handle toggle expand project
  const handleToggleExpand = (projectId) => {
    setExpandedProjectId(projectId)
  }

  // Format last sync time
  const formatLastSync = () => {
    if (!syncStatus?.last_sync_at) return 'Chua dong bo'
    try {
      const date = new Date(syncStatus.last_sync_at)
      return date.toLocaleString('vi-VN')
    } catch {
      return syncStatus.last_sync_at
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-6">
      <SEO
        title="Tài liệu dự án"
        description="Tài liệu bán hàng, bảng giá, chính sách và thông tin dự án bất động sản từ Đất Xanh Miền Trung"
        url="/docs"
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200">
                <HiOutlineFolder className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Tài liệu dự án</h1>
                <p className="text-xs text-gray-500">
                  Cap nhat: {formatLastSync()}
                </p>
              </div>
            </div>
            <button
              onClick={loadProjects}
              disabled={loading}
              className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
              title="Lam moi"
            >
              <HiOutlineRefresh className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tim kiem tai lieu..."
              className="w-full pl-10 pr-10 py-3 bg-gray-100 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 outline-none transition-all"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
              >
                <HiOutlineX className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Results */}
        {searchResults !== null ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-600">
                {searching ? 'Dang tim...' : `Tim thay ${searchResults.length} ket qua`}
              </h2>
              <button
                onClick={handleClearSearch}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Xoa tim kiem
              </button>
            </div>

            {searchResults.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {searchResults.map((file, index) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    variant="grid"
                    onPreview={handlePreviewSearchResult}
                    onCopyLink={handleCopyLink}
                    priority={index < 6}
                  />
                ))}
              </div>
            ) : !searching ? (
              <div className="py-16 text-center">
                <HiOutlineSearch className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Khong tim thay ket qua
                </h3>
                <p className="text-gray-500">
                  Thu tim kiem voi tu khoa khac
                </p>
              </div>
            ) : null}
          </div>
        ) : loading ? (
          // Loading skeleton - 2 columns
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-sm animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gray-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : projects.length > 0 ? (
          // Project list - 2 columns grid, expanded chiếm full width
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onCopyLink={handleCopyLink}
                onPreviewFile={handlePreviewFile}
                isExpanded={expandedProjectId === project.id}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        ) : (
          // Empty state
          <div className="py-20 text-center">
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <HiOutlineFolder className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Chua co du an nao
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              Admin can cau hinh Google Drive va nhan "Reload" de dong bo tai lieu.
            </p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false)
          setPreviewFile(null)
          setPreviewFileList([])
          setPreviewIndex(0)
        }}
        // Navigation props
        onPrevious={handlePreviewPrevious}
        onNext={handlePreviewNext}
        hasPrevious={previewIndex > 0}
        hasNext={previewIndex < previewFileList.length - 1}
        currentIndex={previewIndex}
        totalFiles={previewFileList.length}
      />
    </div>
  )
}

export default ProjectDocs
